import {
  Box,
  Group,
  Loader,
  Paper,
  Stack,
  Tabs,
  Text,
  useMantineTheme,
} from "@mantine/core";
import React, { FC, useEffect, useState } from "react";
import SessionTabPanel from "./SessionTabPanel";
import { ChatSessionValues } from "../hooks/useChatSessions";
import { UserStatus } from "../models/Message";
import { MdCheckCircle, MdDoNotDisturbOn } from "react-icons/md";
import { tokenUsername } from "../utilities/TokenUtil";

interface SessionTabsProps {
  chatSessionValues: ChatSessionValues;
  connectionStatus: string;
  sessionUserStatuses: Map<string, UserStatus>;
  unreadCounts: Map<string, number>;
  lastMessages: Map<string, string>;
}

const SessionTabs: FC<SessionTabsProps> = ({
  chatSessionValues,
  connectionStatus,
  sessionUserStatuses,
  unreadCounts,
  lastMessages,
}) => {
  const [activeTab, setActiveTab] = useState<string | null>("assigned");
  const [username, setUsername] = useState<string | null>(null);

  const theme = useMantineTheme();

  useEffect(() => {
    setUsername(tokenUsername());
  }, []);

  useEffect(() => {
    chatSessionValues.isAssigned
      ? setActiveTab("assigned")
      : setActiveTab("unassigned");
  }, [chatSessionValues.isAssigned]);

  return (
    <Stack w="100%" h="90%">
      <Paper radius="lg" p="sm">
        {username === "superuser" ? (
          <Text c="red">
            Superuser is only able to view historic messages, and cannot chat
            with users or connect to the chat system. Please sign in with a
            normal admin account to chat with users.
          </Text>
        ) : (
          <Group>
            <Text size="md" fw={500}>
              Connection status: {connectionStatus}
            </Text>
            {connectionStatus === "Connected" ? (
              <MdCheckCircle color={theme.colors.green[7]} />
            ) : connectionStatus === "Reconnecting" ? (
              <Loader size="xs" type="bars" />
            ) : (
              <MdDoNotDisturbOn color={theme.colors.red[7]} />
            )}
          </Group>
        )}
      </Paper>

      <Tabs
        value={activeTab}
        onChange={setActiveTab}
        h={username === "superuser" ? "85%" : "90%"}
        w="100%"
      >
        <Tabs.List w="100%">
          <Tabs.Tab
            onClick={() => chatSessionValues.changeSessionAssignment(true)}
            value="assigned"
            w="50%"
          >
            <Text>Assigned chat sessions</Text>
          </Tabs.Tab>
          <Tabs.Tab
            onClick={() => chatSessionValues.changeSessionAssignment(false)}
            value="unassigned"
            w="50%"
          >
            <Text>Unassigned chat sessions</Text>
          </Tabs.Tab>
        </Tabs.List>
        <Box h="95%">
          <SessionTabPanel
            tabValue="assigned"
            chatSessionValues={chatSessionValues}
            sessionUserStatuses={sessionUserStatuses}
            unreadCounts={unreadCounts}
            lastMessages={lastMessages}
          />
          <SessionTabPanel
            tabValue="unassigned"
            chatSessionValues={chatSessionValues}
            sessionUserStatuses={sessionUserStatuses}
            unreadCounts={unreadCounts}
            lastMessages={lastMessages}
          />
        </Box>
      </Tabs>
    </Stack>
  );
};

export default SessionTabs;
